import React from "react";
import { useCMS } from "@/context/CMSContext";

// Tiny base64 placeholder for instant LCP (134 bytes)
const HERO_PLACEHOLDER = "data:image/webp;base64,UklGRj4AAABXRUJQVlA4IDIAAADQAQCdASoKAAsABUB8JYwCdAEU6Yq6AAD+0ygEyyuBGh6hFiTSRrx4BVBL7ILWaYlgAA==";

const HeroSection = () => {
  const { content } = useCMS();
  const hero = content.hero;
  const about = content.about;

  // Optimized hero images - local WebP files
  const heroMobile = "/images/hero/hero_mobile.webp";
  const heroDesktop = "/images/hero/hero_desktop.webp";

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col justify-between overflow-hidden"
      data-testid="hero-section"
      aria-label="Shri Ram Institute of Hotel Management Dehradun"
    >
      {/* Hidden SEO Content */}
      <h1 className="sr-only">Best Hotel Management College in Dehradun, Uttarakhand - Shri Ram Institute</h1>

      {/* Background Image - Instant placeholder + optimized WebP */}
      <div className="absolute inset-0 z-0">
        {/* Instant blur placeholder */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_PLACEHOLDER})`, filter: 'blur(20px)', transform: 'scale(1.1)' }}
          aria-hidden="true"
        />
        {/* Main optimized image */}
        <picture>
          <source media="(max-width: 768px)" srcSet={heroMobile} type="image/webp" />
          <source media="(min-width: 769px)" srcSet={heroDesktop} type="image/webp" />
          <img
            src={heroMobile}
            alt="Student at Taj Hotel - Shri Ram Institute Dehradun"
            className="absolute inset-0 w-full h-full object-cover object-center"
            width="800"
            height="894"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(345,60%,15%)/0.85] via-[hsl(345,60%,20%)/0.6] to-[hsl(345,60%,15%)/0.8] md:bg-none" />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-l from-[hsl(345,60%,15%)/0.95] via-[hsl(345,60%,20%)/0.7] to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        
        {/* Caption */}
        <div className="absolute bottom-4 left-4 md:left-auto md:bottom-24 md:left-20 z-20 bg-white/90 backdrop-blur px-3 py-1.5 md:px-4 md:py-2 rounded-lg shadow-lg">
          <p className="text-[hsl(345,60%,35%)] text-xs md:text-sm font-medium italic">
            ✨ Actual image of our student at Taj
          </p>
        </div>
      </div>

      {/* Logo - Top Left Corner of Hero Image */}
      <div className="absolute top-20 left-4 md:top-24 md:left-8 z-20">
        <div className="bg-white/95 backdrop-blur-sm px-4 py-3 md:px-5 md:py-4 rounded-lg shadow-lg">
          <div className="text-[hsl(345,60%,35%)] text-center" itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="Shri Ram Institute of Hotel Management Dehradun" />
            <meta itemProp="address" content="Rajpur Road, Dehradun, Uttarakhand 248001" />
            <div className="flex items-baseline justify-center">
              <span className="font-serif text-lg md:text-xl font-medium">श्री</span>
              <span className="font-display text-3xl md:text-4xl font-bold tracking-tight ml-1">RAM</span>
            </div>
            <div className="font-display text-xs md:text-sm font-semibold tracking-[0.25em] -mt-1">INSTITUTE</div>
            <div className="font-display text-[10px] md:text-xs font-medium tracking-[0.2em]">DEHRADUN</div>
            <div className="w-14 md:w-16 h-px bg-[hsl(345,60%,35%)] my-1.5 mx-auto"></div>
            <div className="font-display text-[9px] md:text-[10px] tracking-[0.15em] text-[hsl(345,60%,35%)/0.8]">EST. {about.established || "1999"}</div>
          </div>
        </div>
      </div>

      {/* Top Content - Empty space for image visibility */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32">
        <div className="hidden lg:block" />
      </div>

      {/* Bottom Content - Jobs text only */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
          {/* Left Side - Empty for Desktop */}
          <div className="hidden lg:block" />

          {/* Content - Center on mobile, Right on desktop */}
          <div className="text-center md:text-left lg:text-right">
            {/* Admissions Open Badge */}
            <div className="mb-3 animate-fade-in-up">
              <div className="inline-block bg-[hsl(345,60%,35%)] text-white px-4 py-2 rounded-lg shadow-lg">
                <span className="font-display text-sm md:text-base font-bold tracking-wide">Admissions Open 2026</span>
              </div>
            </div>
            
            {/* Dynamic Tagline from CMS */}
            {hero.tagline && (
              <div className="mb-3 animate-fade-in-up delay-100">
                <div className="inline-block bg-[hsl(43,74%,45%)] text-white px-4 py-2 rounded-lg shadow-lg">
                  <span className="font-display text-sm md:text-base font-bold tracking-wide">{hero.tagline}</span>
                </div>
              </div>
            )}
            
            {/* Jobs & Internships text */}
            <div className="animate-fade-in-up delay-200">
              <div className="inline-block bg-white/95 backdrop-blur px-4 py-2 md:px-6 md:py-3 rounded-xl shadow-lg">
                <p className="font-display text-base sm:text-lg md:text-xl lg:text-2xl font-bold">
                  <span className="text-[hsl(345,60%,35%)]">Jobs & Internships</span>
                  <span className="text-[hsl(0,0%,30%)]"> with </span>
                  <span className="text-[hsl(43,74%,45%)]">5 Star Hotels</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 md:bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <div className="w-5 h-8 md:w-6 md:h-10 border-2 border-white/50 rounded-full flex justify-center bg-white/10 backdrop-blur">
          <div className="w-1 h-2 md:h-3 bg-white rounded-full mt-1.5 md:mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
